import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {GeneralEntity} from '../../../generalEntity';
import {GeneralService} from '../../../services/general.service';
import { MessageService } from '../../../../shared/message.service';
import {ServiceAnswer} from '../../../../shared/service-answer';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {ModalWindowComponent} from '../../common/modal-window/modal-window';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ButtonMeta} from '../../common/buttons/buttonMeta';
import {BaseComponent} from '../base/base.component';
import {Subscription} from 'rxjs';
import {ServiceTools} from '../../../services/serviceTools';
import {UiContext} from '../../../../shared/ui-settings.service';
import { ViewContext } from '../../../../shared/view-context';
import { getPrefixedViewUrl } from '../url-helper';

@Component({
  selector: 'qm-general-list',
  templateUrl: './generalList.component.html',
  styleUrls: ['./generalList.component.css']
})
export class GeneralListComponent<ENTITY extends GeneralEntity> extends BaseComponent implements OnInit, OnDestroy {

  public entities: any[]; // List of entities in array
  public entityOrder: { columnId: any, ascending: boolean }; // List of entities in array
  public selectedEntity: any; // The selected entity in array
  private listColumns: { id: string, title: string }[];
  private navigationSubscription: Subscription;

  /**
   * Constructor. Initializes the needed services and other components.
   * @param generalService The service that is used to add, modify and fetch entities.
   * @param router Router used to navigate to different urls.
   * @param messagesService MessageService used to add messages.
   * @param route Route used to get url parameters.
   * @param viewContext The context used to build ui.
   */
  constructor(private generalService: GeneralService<ENTITY>,
              private router: Router,
              protected messagesService: MessageService,
              private route: ActivatedRoute,
              public modalService: NgbModal,
              public viewContext: ViewContext
  ) {
    super();
  }

  /**
   * When an entity has been selected. Sets the selectedEntity reference to point to given entity.
   * @param entity The selected entity.
   */
  onSelect(entity: any): void {
    this.selectedEntity = entity;
  }

  /**
   * When a header has been clicked.
   * @param entity The clicked header.
   */
  onHeaderClicked(headerId: any): void {
    if (!this.entityOrder) {
      this.entityOrder = {ascending: true, columnId: headerId};
    } else {
      // If header is the same as before invert the order. Otherwise set ascending as default.
      this.entityOrder.ascending = this.entityOrder.columnId === headerId ? !this.entityOrder.ascending : true;
      this.entityOrder.columnId = headerId;
    }
    // Multiplier is used to invert the sign of compare outcome
    const multiplier: number = this.entityOrder.ascending ? 1 : -1;
    this.entities.sort((entity1: ENTITY, entity2: ENTITY) => {
      // If number then sort by numeric value
      if (typeof entity1[headerId] === 'number') {
        return (entity1[headerId] - entity2[headerId]) * multiplier;
      }
      // If not number then convert to string and sort as string
      return (('' + entity1[headerId]).localeCompare('' + entity2[headerId])) * multiplier;
    });
  }

  /**
   * Returns the list of columns used in entity table. If the viewContext is set the columns are created based
   * on it. Otherwise the default columns are returned
   */
  getListColumns(): { id: string, title: string }[] {
    if (this.viewContext && this.viewContext.tableColumns) {
      return this.viewContext.tableColumns;
    } else {
      if (!!this.listColumns) {
        return this.listColumns;
      } else {
        this.listColumns = [
          {id: 'id', title: 'id'},
          {id: 'name', title: 'name'},
        ];
        return this.listColumns;
      }
    }
  }

  /**
   * Returns the id of the entity. By default the id attrivute of the entity is returned.
   * @param entity The entity from which the id is returned.
   * @returns string The id value.
   */
  protected getIdOfEntity(entity: ENTITY): string {
    return '' + entity.id;
  }

  /**
   * Opens the currently selected entity in editor. Navigates to the editor view.
   */
  openCurrentEntityInForm(entity: ENTITY): void {
    if (!entity) {
      return;
    }
    this.router.navigateByUrl(this.getViewUrl(this.getIdOfEntity(entity)));
  }

  /**
   * Opens a new entity in editor and navigates to the editor view.
   */
  openNewEntityInForm(): void {
    this.router.navigateByUrl(this.getViewUrl('new'));
  }

  /**
   * Returns the form url for given id.
   * @param id The id of the entity whose form url is requested.
   */
  protected getViewUrl(id: number | string): string {
    return getPrefixedViewUrl(`/${this.viewContext.formUrl}/${id}`);
  }

  /**
   * Opens a dialog that contains question if the given entity should be removed.
   */
  openEntityInDeleteDialog(row: any): void {
    const selected: ENTITY = row;
    // const selected: ENTITY = this.selectedEntity;
    const id: string = this.getIdOfEntity(selected);
    // const name: string = selected['name'];
    const ngbModalRef: NgbModalRef = this.modalService.open(ModalWindowComponent);
    this.increaseOpenView();
    ngbModalRef.componentInstance.title = `Delete entity ${selected['name'] ? selected['name'] : ''} (${id ? id : 'unknown id'})?`;
    ngbModalRef.componentInstance.text = `Are you sure that you want to delete the selected ${this.viewContext.entityNameSingular}?`;

    ngbModalRef.componentInstance.buttons = [
      {text: 'Cancel', id: 'cancel', focus: true},
      {text: 'Delete', id: 'delete'},
    ];
    ngbModalRef.componentInstance.closed = async (buttonId: string) => {
      ngbModalRef.close();
      if (buttonId === 'delete') {
        this.deleteEntity(id);
      }
    };
    ngbModalRef.result.then((result) => {
      this.decreaseOpenView();
    }, (reason) => {
      this.decreaseOpenView();
    });
  }

  /**
   * Opens a dialog that contains question if the given entity should be removed.
   */
  openEntityInCopyToDialog(row: any): void {
    const selected: ENTITY = row;
    // const selected: ENTITY = this.selectedEntity;
    const id: string = this.getIdOfEntity(selected);
    // const name: string = selected['name'];
    const ngbModalRef: NgbModalRef = this.modalService.open(ModalWindowComponent);
    this.increaseOpenView();
    ngbModalRef.componentInstance.title = `Copy entity ${selected['name'] ? selected['name'] : ''} (${id ? id : 'unknown id'})?`;
    ngbModalRef.componentInstance.text = `Select the context to which to copy the selected ${this.viewContext.entityNameSingular}:`;
    ngbModalRef.componentInstance.showContexts = true;

    ngbModalRef.componentInstance.buttons = [
      {text: 'Cancel', id: 'cancel', focus: true},
      {text: 'Copy', id: 'copy', contextNeeded: true},
    ];
    ngbModalRef.componentInstance.closed = async (buttonId: string, uiContext: UiContext) => {
      ngbModalRef.close();
      if (buttonId === 'copy') {
        await this.copyEntityToUiContext(this.selectedEntity, uiContext);
      }
    };
    ngbModalRef.result.then((result) => {
      this.decreaseOpenView();
    }, (reason) => {
      this.decreaseOpenView();
    });
  }

  /**
   * Copies the given entity to given context.
   * @param entity The entity to be copied.
   * @param uiContext The context to which to copy the entity.
   */
  private async copyEntityToUiContext(entity: ENTITY, uiContext: UiContext): Promise<void> {
    const name: string = this.selectedEntity.name;
    try {
      const serviceAnswer: ServiceAnswer<any> = await this.generalService.addEntity(entity, null, uiContext);
      if (serviceAnswer.isOk) {
        this.messagesService.addSuccess(`Successfully copied the entity ${name} to context ${uiContext.name}`);
        this.fetchEntities();
        return;
      } else {
        const errorMessage = ServiceTools.getErrorMessage(serviceAnswer, `Copying the entity ${name} to context ${uiContext.name} failed.`);
        console.error(errorMessage);
        this.messagesService.addDanger(errorMessage);
      }
    } catch (error) {
      const message = `Unexpected error when copying the entity ${name} to context ${uiContext.name}: ${JSON.stringify(error)}`;
      console.error(message);
      this.messagesService.addDanger(message);
    }
  }

  /**
   * When a control button has been pressed.
   * @param buttonId The id of the button.
   */
  controlButtonPressed(buttonId: string) {
    if (buttonId === GeneralListComponent.CONTROL_BUTTON_ID_EDIT) {
      this.openCurrentEntityInForm(this.selectedEntity);
    } else if (buttonId === GeneralListComponent.CONTROL_BUTTON_ID_ADD) {
      this.openNewEntityInForm();
    } else if (buttonId === GeneralListComponent.CONTROL_BUTTON_ID_DELETE) {
      this.openEntityInDeleteDialog(this.selectedEntity);
    } else if (buttonId === GeneralListComponent.CONTROL_BUTTON_ID_COPY_TO) {
      this.openEntityInCopyToDialog(this.selectedEntity);
    }
  }

  /**
   * Returns array of button meta objects. These objects are used to create buttons.
   */
  public getButtonsMeta(): ButtonMeta[] {
    return [
      {id: GeneralListComponent.CONTROL_BUTTON_ID_EDIT, text: 'Edit', type: 'general', disableWhenNoSelection: true},
      {id: GeneralListComponent.CONTROL_BUTTON_ID_ADD, text: 'Add', type: 'general', disableWhenNoSelection: false},
      {id: GeneralListComponent.CONTROL_BUTTON_ID_DELETE, text: 'Delete', type: 'delete', disableWhenNoSelection: true},
      {id: GeneralListComponent.CONTROL_BUTTON_ID_COPY_TO, text: 'Copy to', type: 'general', disableWhenNoSelection: true}
    ];
  }

  /**
   * Removes the entity with the given id.
   * @param id The id of the entity to be removed.
   */
  async deleteEntity(id: number | string): Promise<void> {
    const name: string = this.selectedEntity.name;
    try {
      const serviceAnswer: ServiceAnswer<any> = await this.generalService.removeEntity(id, this.getRestParameters());
      if (serviceAnswer.isOk) {
        this.messagesService.addSuccess('Successfully deleted entity ' + name + ' (' + id + ')');
        this.fetchEntities();
        return;
      } else {
        const errorMessage = ServiceTools.getErrorMessage(serviceAnswer, `Deleting the entity ${name} (${id}) failed.`);
        console.error(errorMessage);
        this.messagesService.addDanger(errorMessage);
      }
    } catch (error) {
      const message: string = 'Unexpected error when deleting the entity ' + name + ' (' + id + '):' + JSON.stringify(error);
      console.error(message);
      this.messagesService.addDanger(message);
    }
  }

  /**
   * Fetches entities from the service.
   */
  async fetchEntities(): Promise<void> {
    try {
      const serviceAnswer: ServiceAnswer<any> = await this.generalService.getEntities(this.getRestParameters());
      this.entities = ServiceTools.getEntitiesFromFirstContext(serviceAnswer, this.messagesService, this.viewContext.entityNamePlural);
    } catch (error) {
      const message: string = 'Unexpected error when fetching entities:' + JSON.stringify(error);
      console.error(message);
      this.messagesService.addDanger(message);
    }
  }

  /**
   * Returns a component that can be used to openApplicationCacheDeleteDialog modal windows.
   * @returns Service or component that is used to openApplicationCacheDeleteDialog modal windows.
   */
  getModalService(): NgbModal {
    return this.modalService;
  }

  /**
   * Returns rest parameters. By default null value is returned. Objects extending this
   * class may overwrite this function if needed.
   */
  protected getRestParameters(): { key: string; value: string }[] {
    return null;
  }

  /**
   * Return the id from the route.
   */
  protected getIdFromRoute(): number | string {
    return this.route.snapshot.paramMap.get('id');
  }

  /**
   * Inits the component. Entities are fetched. If the entity id is given in url the given entity
   * is selected in the array.
   */
  async ngOnInit(): Promise<void> {
    await this.fetchEntities();
    const id: number | string = this.getIdFromRoute();
    if (id) {
      const entities: any[] = this.entities.filter(entity => this.getIdOfEntity(entity) === id);
      if (entities && entities.length > 0) {
        this.selectedEntity = entities[0];
      }
    }

    /**
     * When the page is reload the entities are fetched. This happens when the context
     * (develop / production) has been changed.
     */
    this.navigationSubscription = this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.contextChanged();
      }
    });
  }

  /**
   * When the context has changed.
   */
  protected contextChanged(): void {
    this.fetchEntities();
  }

  /**
   * When the view is destroyed. Navigation subscription is unsubscribed.
   */
  ngOnDestroy(): void {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }
}
